import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { getCountryNameFromTwoLetterCode } from '@eventbrite/intl';
import { CheckoutState } from '../../state';
import { createSelector, getNamespaceValue } from '../utils/selector';

/**
 * @return `true` if the `userLocale` is determined to be the same as the country.
 */
const _isLocaleInCountry = (country: string, userLocale: string): boolean => {
    if (userLocale && country) {
        return userLocale.slice(-2).toLowerCase() === country.toLowerCase();
    }

    return false;
};

/**
 * Return a formatted string representing the event's location. If the user's locale is
 * the same as the country for the event venue, don't display the country to the user.
 *
 * @param localizedAreaDisplay The area information for the event venue location. Example: "Palm Springs, CA"
 * @param country The country code for the event venue location. Example: "US"
 * @param userLocale The locale of the browser. Example: "en_US"
 * @return Formatted location string. Example: "Palm Springs, CA, United States"
 */
const _getFormattedEventLocation = (
    eventInfo: { localizedAreaDisplay?: string; country?: string } = {},
    userLocale: string,
): string | GenericLazyString => {
    const { localizedAreaDisplay, country } = eventInfo;

    if (localizedAreaDisplay && country) {
        const localizedCountryName = getCountryNameFromTwoLetterCode(country);

        // Translators: The 'Area' is the full city name. For example, 'Palm Springs, CA'.
        const localizedAreaWithCountry = gettext(
            '%(localizedAreaDisplay)s, %(localizedCountryName)s',
            {
                localizedAreaDisplay,
                localizedCountryName,
            },
        );

        return _isLocaleInCountry(country, userLocale)
            ? localizedAreaDisplay
            : localizedAreaWithCountry;
    }

    return '';
};

/**
 * Selector that can be used to retrieve the event's location formatted for user's locale.
 */
export const getFormattedEventLocation = createSelector(
    (state: CheckoutState) => getNamespaceValue(state.event, 'venue.address'),
    (state: CheckoutState) => state.app.locale,
    ((
        address: {
            country: string;
            localizedAreaDisplay: string;
        },
        userLocale: string,
    ) => _getFormattedEventLocation(address, userLocale)) as unknown as any,
);
